import {closestCenter} from '@dnd-kit/core'
import {DndContext} from '@dnd-kit/core'
import {KeyboardSensor} from '@dnd-kit/core'
import {PointerSensor} from '@dnd-kit/core'
import {TouchSensor} from '@dnd-kit/core'
import {useSensors} from '@dnd-kit/core'
import {useSensor} from '@dnd-kit/core'
import {SortableContext} from '@dnd-kit/sortable'
import {sortableKeyboardCoordinates} from '@dnd-kit/sortable'
import {useSortable} from '@dnd-kit/sortable'
import {verticalListSortingStrategy} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import React from 'react'

import useCloseOnClickOutside from '../hooks/use-close-on-click-outside'
import bem from '../utils/bem'
import {calculateRankAfter} from '../utils/calculate-rank'
import {calculateRankBefore} from '../utils/calculate-rank'
import {calculateRankBetween} from '../utils/calculate-rank'
import genId from '../utils/gen_id'
import get from '../utils/get'
import useHash from '../utils/use-hash'
import AddIcon from './add-icon'
import AddSetModal from './add-set-modal'
import Button from './button'
import Congratulations from './congratulations'
import Executor from './executor'
import HamburgerIcon from './hamburger-icon'
import {useInit} from './init-context'
import {useDelete} from './object-store'
import {useList} from './object-store'
import {useObjectStore} from './object-store'
import {useSave} from './object-store'
import {useSaveListener} from './object-store'
import PopupMenu from './popup-menu'
import sorted from './sorted'
import TimerConfigurationModal from './timer-configuration-modal'
import TemplatesModal from './templates-modal'
import TimerIcon from './timer2-icon'
import Timer from './timer'
import {makeSound} from './timer'
import useSpinning from './use-spinning'
import WrktitleEditor from './wrktitle-editor'
import WrktsetEditor from './wrktset-editor'
import DoneIcon from './done-icon'
import EditIcon from './edit2-icon'
import GrabberIcon from './grabber-icon'
import TemplateIcon from './template2-icon'
import ChainIcon from './chain2-icon'
import CollapseIcon from './collapse-icon'
import SlidingBox from './sliding-box'
import DeleteIcon from './trashcan2-icon'
import Today2Icon from './today2-icon'
import Week2Icon from './week2-icon'
import All2Icon from './all2-icon'
import './log.scss'
import './switcher.scss'


export default function Log() {
    const {
        increaseSpinningCounter,
        decreaseSpinningCounter,
        spinning,
    } = useSpinning()
    const init = useInit()
    const save = useSave()
    const remove = useDelete()
    const {params, setHashParam} = useHash()
    const {state} = useObjectStore()
    const wrktsets = useList('wrktset', {})
    const wrktitles = useList('wrktitle', {})
    const notes = useList('note', {})
    const notesRefreshRef = React.useRef(null)
    notesRefreshRef.current = notes.refresh
    useSaveListener(
        React.useMemo(() => ({models: {note: {}}}), []),
        React.useCallback(() => notesRefreshRef.current?.(), [])
    )
    const [activeWrktsetId, setActiveWrktsetId] = React.useState(null)
    const [adderDay, setAddDay] = React.useState(
        new Date().toISOString().split('T')[0]
    )
    const [executor, setExecutor] = React.useState(null)

    const [editMode, setEditMode] = React.useState(false)
    const [selectedIds, setSelectedIds] = React.useState(new Set())
    const [collapsedDays, setCollapsedDays] = React.useState({})
    const [uncollapsedDays, setUncollapsedDays] = React.useState({})
    const [showCongratulations, setShowCongratulations] = React.useState(false)
    const [showAddSetModal, setShowAddSetModal] = React.useState(false)
    const [showTimerConfigurationModal, setShowTimerConfigurationModal] = React.useState(false)
    const [timerActive, setTimerActive] = useLocalStorageState('timer-active', false)
    const [syncRepsAndWeightsInSets, setSyncRepsAndWeightsInSets] = useLocalStorageState('syncRepsAndWeightsInSets', false)
    const [timerDuration, setTimerDuration] = useLocalStorageState('timer-duration', 30)
    const [timerEnd, setTimerEnd] = React.useState(null)
    const timerTimoutRef = React.useRef()

    const days = React.useMemo(() => {
        if (!wrktsets) {return []}
        if (!wrktitles) {return []}
        if (!state) {return []}

        const daysMap = {}
        wrktsets.ids.map(id => state[id]).forEach(wrktset => {
            if (!wrktset) {return}
            daysMap[wrktset.day] ||= {day: wrktset.day, items: []}
            daysMap[wrktset.day].items.push({model: 'wrktset', ...wrktset})
        })
        wrktitles.ids.map(id => state[id]).forEach(wrktitle => {
            if (!wrktitle) {return}
            daysMap[wrktitle.day] ||= {day: wrktitle.day, items: []}
            daysMap[wrktitle.day].items.push({model: 'wrktitle', ...wrktitle})
        })

        const days = Object.values(daysMap).map(d => ({
            day: d.day,
            items: sorted(d.items, i => i.rank),
        }))

        return sorted(days, d => d.day, -1)
    }, [state, wrktsets, wrktitles])

    const dayTitles = React.useMemo(() => {
        const dayTitles = {}
        wrktitles.ids.map(i => {
            const day = state[i]
            if (!day) {return}
            dayTitles[day.day] = day.title
        })
        return dayTitles
    }, [wrktitles])

    const exerciseHistory = React.useMemo(() => {
        const exerciseHistory = {}
        if (!wrktsets) {return []}
        if (!state) {return []}

        wrktsets.ids.forEach(id => {
            const wrktset = state[id]
            if (!wrktset) {return}
            const title = dayTitles[wrktset.day]
            exerciseHistory[wrktset.exercise] ||= []
            exerciseHistory[wrktset.exercise].push({...wrktset, title})
        })
        Object.keys(exerciseHistory).forEach(e => {
            exerciseHistory[e].sort((a, b) => {
                if (a.day !== b.day) {
                    return a.day > b.day ? -1 : 1
                }
                return a.rank > b.rank ? 1 : a.rank < b.rank ? -1 : 0
            })
        })

        return exerciseHistory
    }, [state, wrktsets])

    const exerciseNotes = React.useMemo(() => {
        const map = {}
        if (!notes || !state) {return map}
        notes.ids.forEach(id => {
            const note = state[id]
            if (!note) {return}
            map[note.exercise] = note
        })
        // pick up optimistically-saved notes not yet in notes.ids
        Object.values(state).forEach(obj => {
            if (obj && obj.exercise && typeof obj.note !== 'undefined' && !map[obj.exercise]) {
                map[obj.exercise] = obj
            }
        })
        return map
    }, [state, notes])


    const filteredDays = React.useMemo(() => {
        if (params.day === 'today') {
            const today = new Date().toISOString().split('T')[0]
            return days.filter(d => d.day === today)
        }
        if (params.day === 'week') {
            const monday = getLastMonday().toISOString().split('T')[0]
            return days.filter(d => d.day >= monday)

        }
        else if (params.day) {
            return days.filter(d => d.day === params.day)
        }
        return days
    }, [days, params.day])

    const actionsDisabled = selectedIds.size === 0


    const toggleEditMode = React.useCallback(function toggleEditMode() {
        setEditMode(prev => !prev)
        setSelectedIds(new Set())
    }, [])

    useKeyPress(true, 'e', toggleEditMode)
    const openTemplates = React.useCallback(() => setHashParam('#', 'templates'), [])
    useKeyPress(true, 'o', openTemplates)
    const openToday = React.useCallback(() => setHashParam('day', 'today'), [])
    useKeyPress(true, 't', openToday)
    const openWeek = React.useCallback(() => setHashParam('day', 'week'), [])
    useKeyPress(true, 'w', openWeek)
    const openAll = React.useCallback(() => setHashParam('day', null), [])
    useKeyPress(true, 'a', openAll)
    const doRepeat = React.useCallback(() => handleBatchAction('repeat'))
    useKeyPress(editMode && !actionsDisabled, 'r', doRepeat)
    const doReschedule = React.useCallback(() => handleBatchAction('reschedule'))
    useKeyPress(editMode && !actionsDisabled, 's', doReschedule)
    const doDelete = React.useCallback(() => handleBatchAction('delete'))
    useKeyPress(!actionsDisabled, 'Backspace', doDelete)
    useKeyPress(!actionsDisabled, 'Delete', doDelete)

    function toggleSelectId(id) {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    function handleBatchAction(action) {
        const wrktitleIdSet = new Set(wrktitles.ids)
        const selectedWrktsetList = [...selectedIds].filter(id => !wrktitleIdSet.has(id)).map(id => state[id]).filter(Boolean)
        const selectedWrktitleList = [...selectedIds].filter(id => wrktitleIdSet.has(id)).map(id => state[id]).filter(Boolean)

        if (action === 'repeat today') {
            Promise.all([
                ...selectedWrktsetList.map(ws =>
                    save('wrktset', {...ws, user: undefined, id: genId(), rank: -1, day: getToday(), done_at: null, status: 'todo'})
                ),
                ...selectedWrktitleList.map(wt =>
                    save('wrktitle', {...wt, user: undefined, id: genId(), rank: -1, day: getToday()})
                ),
            ]).then(() => { wrktsets.refresh(); wrktitles.refresh(); setSelectedIds(new Set()) })
        } else if (action === 'repeat') {
            // Repeat titles: insert after the last selected title
            const newTitleIdMap = {}
            const sortedTitles = [...selectedWrktitleList].sort((a, b) => a.rank > b.rank ? 1 : -1)
            let titleSavePromises = []
            if (sortedTitles.length > 0) {
                const lastTitle = sortedTitles[sortedTitles.length - 1]
                const allTitles = wrktitles.ids.map(id => state[id]).filter(Boolean)
                const nextTitle = allTitles
                    .filter(t => t.day === lastTitle.day && t.rank > lastTitle.rank && !selectedIds.has(t.id))
                    .sort((a, b) => a.rank > b.rank ? 1 : -1)[0]
                let prevTitleRank = lastTitle.rank
                const nextTitleRank = nextTitle?.rank
                titleSavePromises = sortedTitles.map(wt => {
                    const newId = genId()
                    newTitleIdMap[wt.id] = newId
                    const rank = nextTitleRank
                        ? calculateRankBetween(prevTitleRank, nextTitleRank)
                        : calculateRankAfter(prevTitleRank)
                    prevTitleRank = rank
                    return save('wrktitle', {...wt, user: undefined, id: newId, rank})
                })
            }

            // Repeat sets: insert after the last selected set
            const sortedSets = [...selectedWrktsetList].sort((a, b) => a.rank > b.rank ? 1 : -1)
            const newSetIds = sortedSets.map(() => genId())
            let setSavePromises = []
            if (sortedSets.length > 0) {
                const lastSet = sortedSets[sortedSets.length - 1]
                const allSets = wrktsets.ids.map(id => state[id]).filter(Boolean)
                const nextSet = allSets
                    .filter(s => s.day === lastSet.day
                        && s.rank > lastSet.rank
                        && !selectedIds.has(s.id))
                    .sort((a, b) => a.rank > b.rank ? 1 : -1)[0]
                let prevRank = lastSet.rank
                const nextRank = nextSet?.rank
                setSavePromises = sortedSets.map((ws, i) => {
                    const rank = nextRank
                        ? calculateRankBetween(prevRank, nextRank)
                        : calculateRankAfter(prevRank)
                    prevRank = rank
                    return save('wrktset', {...ws, user: undefined, id: newSetIds[i], rank, done_at: null, status: 'todo'})
                })
            }

            const newTitleIds = Object.values(newTitleIdMap)
            Promise.all([...titleSavePromises, ...setSavePromises])
                .then(() => { wrktsets.refresh(); wrktitles.refresh(); setSelectedIds(new Set([...newTitleIds, ...newSetIds])) })
        } else if (action === 'delete') {
            Promise.all([
                ...selectedWrktsetList.map(ws => remove('wrktset', ws.id)),
                ...selectedWrktitleList.map(wt => remove('wrktitle', wt.id)),
            ]).then(() => { wrktsets.refresh(); wrktitles.refresh(); setSelectedIds(new Set()) })
        } else {
            setExecutor({action, wrktsets: selectedWrktsetList, wrktitles: selectedWrktitleList})
        }
    }

    function handleUpdateStatus(update) {
        if (!update.day) {return}
        if (update.status !== 'done') {
            return
        }
        const dayWrktsets = wrktsets.ids
            .filter(id => state[id].day)
            .filter(id => state[id].day === update.day)
        const dayDone = dayWrktsets
            .filter(id => id !== update.id)
            .every(id => state[id].status === 'done')
        if (dayDone) {
            setShowCongratulations(dayWrktsets.length)
        }
        if (activeWrktsetId === update.id) {
            setActiveWrktsetId(null)
        }
        if (timerActive && !dayDone) {
            setTimerEnd(addSecondsToDate(new Date(), timerDuration))
            clearTimeout(timerTimoutRef.current)
            timerTimoutRef.current = setTimeout(() => makeSound(init.user), timerDuration * 1000)
        }
    }


    return <div className="log">
        <div className="nav">
            <div className="nav__left">
                <SlidingBox open={!editMode}>
                    <Button
                        onClick={() => setSyncRepsAndWeightsInSets(prev => !prev)}
                        active={syncRepsAndWeightsInSets}
                    >
                        <ChainIcon color="white" />
                    </Button>
                    <Button
                        onClick={() => setShowTimerConfigurationModal(true)}
                        dim={!timerActive}
                    >
                        <TimerIcon color="white" />
                    </Button>
                    {timerEnd?
                        <Timer
                            timerEnd={timerEnd}
                            onSetTimerEnd={setTimerEnd}
                            timerTimoutRef={timerTimoutRef}
                        />
                        : null
                    }
                </SlidingBox>
                <SlidingBox open={editMode}>
                    <Button disabled={actionsDisabled} onClick={() => handleBatchAction('repeat')}>repeat</Button>
                    <Button disabled={actionsDisabled} onClick={() => handleBatchAction('reschedule')}>schedule</Button>
                    <Button disabled={actionsDisabled} onClick={() => handleBatchAction('delete')} dangerHover>
                        <DeleteIcon color="white" />
                    </Button>
                </SlidingBox>
            </div>
            <SlidingBox open={!editMode}>
                <div className="switcher">
                    <Button
                        onClick={() => setHashParam('day', 'today')}
                        active={params.day === 'today'}
                        extraClassName="switcher__item"
                    >
                        <Today2Icon color="white" />
                    </Button>
                    <Button
                        onClick={() => setHashParam('day', 'week')}
                        active={params.day === 'week'}
                        extraClassName="switcher__item"
                    >
                        <Week2Icon color="white" />
                    </Button>
                    <Button
                        onClick={() => setHashParam('day', null)}
                        active={!params.day}
                        extraClassName="switcher__item"
                    >
                        <All2Icon color="white" />
                    </Button>
                </div>
                <select
                    className="switcher__select"
                    value={params.day || ''}
                    onChange={e => setHashParam('day', e.target.value || null)}
                >
                    <option value="today">Today</option>
                    <option value="week">Week</option>
                    <option value="">All</option>
                </select>
            </SlidingBox>
            <Button
                disabled={!adderDay || spinning}
                onClick={() => setShowAddSetModal(new Date().toISOString().split('T')[0])}
            >
                <AddIcon color="white" />
            </Button>
            <Button onClick={() => setHashParam('#', 'templates')}>
                <TemplateIcon color="white" />
            </Button>
            <Button
                onClick={toggleEditMode} active={editMode}>{editMode ? <DoneIcon color="white" /> : <EditIcon color="white" />}
            </Button>
        </div>
        <div className="log__inner">
            {filteredDays.map(d => {
                return <WrktDay
                    day={d}
                    key={d.day}
                    onRefresh={wrktsets.refresh}
                    onRefreshTitles={wrktitles.refresh}
                    onActivateWrktset={setActiveWrktsetId}
                    activeWrktsetId={activeWrktsetId}
                    exerciseHistory={exerciseHistory}
                    exerciseNotes={exerciseNotes}
                    onCreateWrkset={wrktsets.refresh}
                    onPrepareExecutor={setExecutor}
                    onShowAddSetModal={setShowAddSetModal}
                    syncRepsAndWeightsInSets={syncRepsAndWeightsInSets}
                    collapsed={!params.day ?
                        !uncollapsedDays[d.day]
                        : collapsedDays[d.day]
                    }
                    onToggleCollapse={!params.day ?
                        (() => setUncollapsedDays(prev => ({...prev, [d.day]: !prev[d.day]})))
                        : (() => setCollapsedDays(prev => ({...prev, [d.day]: !prev[d.day]})))
                    }
                    onUpdateStatus={handleUpdateStatus}
                    setHashParam={setHashParam}
                    editMode={editMode}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelectId}
                />
            })}
        </div>
        {executor ?
            <Executor
                executor={executor}
                onClose={(rDay) => {
                    setExecutor(null)
                    if (rDay) {
                        if (!params.day) {
                            setUncollapsedDays(prev => ({...prev, [rDay]: true}))
                        } else {
                            setCollapsedDays(prev => ({...prev, [rDay]: false}))
                        }
                    }
                }}
                onRefresh={() => {
                    wrktsets.refresh()
                    wrktitles.refresh()
                }}
            />
            : null
        }
        {showCongratulations ?
            <Congratulations
                count={showCongratulations}
                username={init.user}
                onClose={() => setShowCongratulations(null)}
            />
            : null
        }
        {showAddSetModal ?
            <AddSetModal
                onClose={() => setShowAddSetModal(null)}
                onAdd={() => wrktsets.refresh()}
                wrktsets={wrktsets}
                initialDay={showAddSetModal}
            />
            : null
        }
        {showTimerConfigurationModal ?
            <TimerConfigurationModal
                onClose={() => setShowTimerConfigurationModal(false)}
                duration={timerDuration}
                onSetDuration={setTimerDuration}
                active={timerActive}
                onSetActive={setTimerActive}
            />
            : null
        }
        {params['#'] == '#templates' ?
            <TemplatesModal
                onClose={() => setHashParam('#', null)}
                onSchedule={() => {
                    setHashParam('#', null)
                    wrktitles.refresh()
                    wrktsets.refresh()
                }}
            />
            : null
        }
        <p>
            v4
        </p>
    </div>
}

function WrktDay({
    day,
    onRefresh,
    onRefreshTitles,
    onActivateWrktset,
    activeWrktsetId,
    exerciseHistory,
    exerciseNotes,
    onCreateWrkset,
    onPrepareExecutor,
    onShowAddSetModal,
    collapsed,
    onToggleCollapse,
    onUpdateStatus,
    setHashParam,
    syncRepsAndWeightsInSets,
    editMode,
    selectedIds,
    onToggleSelect,
}) {
    const save = useSave()
    const {state} = useObjectStore()

    const allIds = React.useMemo(
        () => day.items.map(i => i.id),
        [day.items]
    )

    const titleIds = React.useMemo(() => {
        const s = new Set()
        day.items.forEach(item => { if (item.model === 'wrktitle') s.add(item.id) })
        return s
    }, [day.items])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {distance: 8},
        }),
        useSensor(TouchSensor, {
            activationConstraint: {delay: 250, tolerance: 5},
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )

    function handleAction(action) {
        if (action === 'add set') {
            onShowAddSetModal(day.day)
        } else if (action === 'add title') {
            addTitle()
        } else if (action === 'create template') {
            createTemplate({day})
        } else {
            onPrepareExecutor({action, day})
        }
    }

    function addTitle() {
        const firstItem = day.items[0]
        const newRank = firstItem ? calculateRankBefore(firstItem.rank) : '1'
        save('wrktitle', {id: genId(), day: day.day, rank: newRank, title: ''})
            .then(() => onRefreshTitles())
    }

    function createTemplate({day}) {
        const firstTitle = day.items.find(i => i.model === 'wrktitle')
        const text = day.items.filter(i => i.model === 'wrktset').map(s => s.exercise).join('\n')
        save('template', {id: genId(), text})
            .then(() => setHashParam('#', 'templates'))
    }

    function moveUp(wrktset) {
        const index = allIds.indexOf(wrktset.id)
        if (index === 0) {return}
        else if (index === 1) {
            const prev = get(state, allIds[0])
            save('wrktset', {id: wrktset.id, rank: calculateRankBefore(prev.rank)})
        }
        else {
            const prevprev = get(state, allIds[index - 2])
            const prev = get(state, allIds[index - 1])
            save('wrktset', {id: wrktset.id, rank: calculateRankBetween(prevprev.rank, prev.rank)})
        }
    }

    function moveDown(wrktset) {
        const l = allIds.length - 1
        const index = allIds.indexOf(wrktset.id)
        if (index === l) {return}
        else if (index === l - 1) {
            const last = get(state, allIds[l])
            save('wrktset', {id: wrktset.id, rank: calculateRankAfter(last.rank)})
        }
        else {
            const next = get(state, allIds[index + 1])
            const nextnext = get(state, allIds[index + 2])
            save('wrktset', {id: wrktset.id, rank: calculateRankBetween(next.rank, nextnext.rank)})
        }
    }

    function handleDragEnd(event) {
        const {active, over} = event
        if (!active || !over || active.id === over.id) {return}

        const overIndex = allIds.indexOf(over.id)
        const activeIndex = allIds.indexOf(active.id)
        const insertIndex = overIndex < activeIndex ? overIndex : overIndex + 1

        let prevRank = null
        let nextRank = null
        for (let i = insertIndex - 1; i >= 0; i--) {
            if (allIds[i] !== active.id) {
                prevRank = get(state, allIds[i])?.rank
                break
            }
        }
        for (let i = insertIndex; i < allIds.length; i++) {
            if (allIds[i] !== active.id) {
                nextRank = get(state, allIds[i])?.rank
                break
            }
        }

        let newRank
        if (prevRank && nextRank) newRank = calculateRankBetween(prevRank, nextRank)
        else if (prevRank) newRank = calculateRankAfter(prevRank)
        else if (nextRank) newRank = calculateRankBefore(nextRank)
        else newRank = '1'

        const model = titleIds.has(active.id) ? 'wrktitle' : 'wrktset'
        save(model, {id: active.id, rank: newRank})
    }

    return <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
    >
        <div className={bem("wrktday").m({collapsed})}>
            <h5 className={bem("wrktitle__title").m({collapsed})}>
                <PopupMenu
                    values={['add set', 'add title', 'reschedule', 'repeat', 'export', 'create template', {value: 'delete', danger: true}]}
                    onSelect={handleAction}
                    background={null}
                >
                    <HamburgerIcon />
                </PopupMenu>
                <div className="wrktitle__title__main">
                    <div className="wrktitle__title__day">
                        {day.day} {collapsed &&
                            <span>
                                {day.items
                                    .filter(i => i.model === 'wrktitle')
                                    .map(i => {
                                        return <span className="wrktitle__title__day__title" key={i.id}>{i.title}</span>
                                    })
                                }
                            </span>
                        }
                    </div>
                </div>
                <Button onClick={onToggleCollapse} round background={null}>
                    <CollapseIcon style={{transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform ease-in-out 0.2s'}} />
                </Button>
            </h5>
            <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
                {!collapsed && day.items.map((item, index) => {
                    if (item.model === 'wrktitle') {
                        return <SortableTitleItem
                            key={item.id}
                            id={item.id}
                            editMode={editMode}
                        >
                            <WrktitleEditor
                                wrktitle={item}
                                day={day.day}
                                editMode={editMode}
                                selected={selectedIds && selectedIds.has(item.id)}
                                onToggleSelect={onToggleSelect}
                            />
                        </SortableTitleItem>
                    }

                    const prevItem = day.items[index - 1]
                    const nextItem = day.items[index + 1]
                    const first = !prevItem || prevItem.model === 'wrktitle' || prevItem.exercise !== item.exercise
                    const last = !nextItem || nextItem.model === 'wrktitle' || nextItem.exercise !== item.exercise

                    const following = syncRepsAndWeightsInSets ?
                        day.items.slice(index).filter(w => w.model === 'wrktset' && w.exercise === item.exercise && w.status !== 'done')
                        : null

                    return <SortableItem
                        id={item.id}
                        key={item.id}
                        first={first}
                        last={last}
                        active={item.id === activeWrktsetId}
                        onActivate={onActivateWrktset}
                        wrktset={item}
                        editMode={editMode}
                        selected={selectedIds ? selectedIds.has(item.id) : false}
                        onToggleSelect={onToggleSelect}
                    >
                        <WrktsetEditor
                            wrktset={item}
                            onMoveUp={moveUp}
                            onMoveDown={moveDown}
                            onActivate={onActivateWrktset}
                            history={exerciseHistory[item.exercise]}
                            exerciseNote={exerciseNotes ? exerciseNotes[item.exercise] : null}
                            active={item.id === activeWrktsetId}
                            onUpdateStatus={onUpdateStatus}
                            following={following}
                            editMode={editMode}
                            selected={selectedIds ? selectedIds.has(item.id) : false}
                            onToggleSelect={onToggleSelect}
                        />
                    </SortableItem>
                })}
            </SortableContext>
        </div>
    </DndContext>
}

function SortableTitleItem({id, editMode, children}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id})

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    return <div ref={setNodeRef} style={style} className="wrktitle-sortable-row">
        <GrabberIcon
            {...attributes}
            {...listeners}
            className={bem('drag-handler-w').m({edit: editMode})}
        />
        {children}
    </div>
}


function SortableItem(props) {
    const ref = React.useRef()
    const ignoreRef = React.useRef()
    useCloseOnClickOutside(props.active && !props.editMode, () => props.onActivate(null), ref, ignoreRef)

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id: props.id})

    if (transform) {
        transform.scaleY = 1
    }

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    return <div ref={ref}>
        <div ref={setNodeRef} style={style} className={bem("sortable-item").m({edit: props.editMode, active: props.active, first: props.first, last: props.last})}>
            <GrabberIcon
                {...attributes}
                {...listeners}
                className={bem('drag-handler-w').m({edit: props.editMode})}
            />
            {props.children}
        </div>
    </div>
}

function getToday() {
    return new Date().toISOString().split('T')[0]
}

function getLastMonday() {
    const today = new Date(); //
    const dayOfWeek = today.getDay()
    const offset = (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
    const lastMonday = new Date(today)
    lastMonday.setDate(today.getDate() - offset)
    return lastMonday;
}


function useLocalStorageState(key, initialValue) {
    const getInitialValue = () => {
      const storedValue = localStorage.getItem(key)
      try {
          return storedValue !== undefined ? JSON.parse(storedValue) : initialValue;
      }
      catch {
        return initialValue
      }
    };

    const [value, setValue] = React.useState(getInitialValue)

    React.useEffect(() => {
      localStorage.setItem(key, JSON.stringify(value))
    }, [key, value]);

    return [value, setValue];
}


function addSecondsToDate(date, seconds) {
  return new Date(date.getTime() + seconds * 1000)
}


function useKeyPress(active, key, callback) {
    React.useEffect(function() {
        if (!active) {return}
        function handleKeyDown(event) {
            if (event.key === key) {
                const target = event.target
                const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable

                if (!isInput) {
                    callback()
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return function() {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [active, callback])
}
